import {reactive} from 'vue';
import BasketItemGroup from '../../types/BasketItemGroup';
import BasketItem from '../../types/BasketItem';
import BasketSummary from '../../types/BasketSummary';
import VoucherItem from '../../types/VoucherItem';
import FreeItems from '../../types/FreeItems';
import ExtraPromotion from '../../types/ExtraPromotion';
import type BasketInformativeBenefitItem from '../../types/BasketInformativeBenefitItem';
import type BasketStoreMessage from '../../types/BasketStoreMessage';
import type CrossSellingProductBoxData from '../../types/CrossSellingProductBoxData';
import type MilesAndMoreModel from '../../types/MilesAndMoreModel';

class BasketStore {
  protected _minimumOrderValueFulFilled: boolean;
  protected _freeShippingFulFilled: boolean;
  protected _error: BasketStoreMessage[];
  protected _basketItemGroups: BasketItemGroup[] = [];
  protected _subscriptionItems: BasketItem[] = [];
  protected _items: BasketItem[] = [];
  protected _freeItems: FreeItems[] = [];
  protected _summary: BasketSummary;
  protected _voucher: VoucherItem;
  protected _giftVoucher: VoucherItem[] = [];
  protected _extraPromotions: ExtraPromotion[] = [];
  protected _paypalExpressAllowed: boolean;
  protected _paymentMethod: string;
  protected _deliveryOption: string;
  protected _commonVoucherErrorMessage: string;
  protected _pureAtypicalDeliveryBasket: boolean;
  protected _deliverySplit: boolean;
  protected _crossSellingViewmodels: CrossSellingProductBoxData[] = [];
  protected _informativeBenefits: BasketInformativeBenefitItem[] = [];
  protected _greetingTextNotAllowedBasket: boolean;
  protected _basketGroupErrors: {index: number, value: string, type: 'minValue'|'minNoOfBottles'}[] = [];
  protected _deliveryPromise: any;
  protected _milesAndMoreViewmodel: MilesAndMoreModel;

  constructor() {
    if (window.__checkoutConfig && window.__checkoutConfig.basket) {
      this.setData(window.__checkoutConfig.basket);
    } else {
      // @ts-ignore
      this._summary = {};
      // @ts-ignore
      this._voucher = {};
    }
  }

  setData(data: BasketStore) {
    this.minimumOrderValueFulFilled = data.minimumOrderValueFulFilled;
    this.freeShippingFulFilled = data.freeShippingFulFilled;
    this.error = data.error;
    this.basketItemGroups = data.basketItemGroups;
    this.subscriptionItems = data.subscriptionItems;
    this.items = data.items;
    this.freeItems = data.freeItems;
    this.voucher = data.voucher;
    this.giftVoucher = data.giftVoucher;
    this.summary = data.summary;
    this.extraPromotions = data.extraPromotions;
    this.paypalExpressAllowed = data.paypalExpressAllowed;
    this.paymentMethod = data.paymentMethod;
    this.deliveryOption = data.deliveryOption;
    this.commonVoucherErrorMessage = data.commonVoucherErrorMessage;
    this.pureAtypicalDeliveryBasket = data.pureAtypicalDeliveryBasket;
    this.deliverySplit = data.deliverySplit;
    this.crossSellingViewmodels = data.crossSellingViewmodels;
    this.informativeBenefits = data.informativeBenefits;
    this.greetingTextNotAllowedBasket = data.greetingTextNotAllowedBasket;
    this.deliveryPromise = data.deliveryPromise;
    this.milesAndMoreViewmodel = data.milesAndMoreViewmodel;

    this._basketGroupErrors = this.getBasketPositionGroupErrors();
  }

  get basketItemGroupsAsList(): BasketItem[] {
    let temp = [];
    for (let i = 0, l = this._basketItemGroups.length; i < l; i += 1) {
      temp.push(...this._basketItemGroups[i].basketItems);
    }
    return temp;
  }

  get subscriptionItems(): BasketItem[] {
    return this._subscriptionItems;
  }

  set subscriptionItems(subscriptionItems: BasketItem[]) {
    this._subscriptionItems = subscriptionItems;
  }

  get basketItemGroups(): BasketItemGroup[] {
    return this._basketItemGroups;
  }

  set basketItemGroups(basketItemGroups: BasketItemGroup[]) {
    this._basketItemGroups = basketItemGroups;
  }

  get items(): BasketItem[] {
    return this._items;
  }

  set items(items: BasketItem[]) {
    this._items = items;
  }

  get freeItems(): FreeItems[] {
    return this._freeItems;
  }

  set freeItems(freeItems: FreeItems[]) {
    this._freeItems = freeItems;
  }

  get voucher(): VoucherItem {
    return this._voucher;
  }

  get giftVoucher(): VoucherItem[] {
    return this._giftVoucher;
  }

  set voucher(voucher: VoucherItem) {
    this._voucher = voucher;
  }

  set giftVoucher(giftVoucher: VoucherItem[]) {
    this._giftVoucher = giftVoucher;
  }

  get paymentMethod(): string {
    return this._paymentMethod;
  }

  set paymentMethod(paymentMethod: string) {
    this._paymentMethod = paymentMethod;
  }

  get deliveryOption(): string {
    return this._deliveryOption;
  }

  set deliveryOption(deliveryOption: string) {
    this._deliveryOption = deliveryOption;
  }

  get commonVoucherErrorMessage(): string {
    return this._commonVoucherErrorMessage;
  }

  set commonVoucherErrorMessage(commonVoucherErrorMessage: string) {
    this._commonVoucherErrorMessage = commonVoucherErrorMessage;
  }

  get pureAtypicalDeliveryBasket(): boolean {
    return this._pureAtypicalDeliveryBasket;
  }

  set pureAtypicalDeliveryBasket(pureAtypicalDeliveryBasket: boolean) {
    this._pureAtypicalDeliveryBasket = pureAtypicalDeliveryBasket;
  }

  get summary(): BasketSummary {
    return this._summary;
  }

  set summary(summary: BasketSummary) {
    this._summary = summary;
  }

  get freeShippingFulFilled(): boolean {
    return this._freeShippingFulFilled;
  }

  set freeShippingFulFilled(freeShippingFulFilled: boolean) {
    this._freeShippingFulFilled = freeShippingFulFilled;
  }

  get minimumOrderValueFulFilled(): boolean {
    return this._minimumOrderValueFulFilled;
  }

  set minimumOrderValueFulFilled(minimumOrderValueFulFilled: boolean) {
    this._minimumOrderValueFulFilled = minimumOrderValueFulFilled;
  }

  get error(): BasketStoreMessage[] {
    return this._error;
  }

  set error(error: BasketStoreMessage[]) {
    this._error = error;
  }

  get extraPromotions(): ExtraPromotion[] {
    return this._extraPromotions;
  }

  set extraPromotions(extraPromotions: ExtraPromotion[]) {
    this._extraPromotions = extraPromotions;
  }

  get paypalExpressAllowed(): boolean {
    return this._paypalExpressAllowed;
  }

  set paypalExpressAllowed(paypalExpressAllowed: boolean) {
    this._paypalExpressAllowed = paypalExpressAllowed;
  }

  get deliverySplit(): boolean {
    return this._deliverySplit;
  }

  set deliverySplit(deliverySplit: boolean) {
    this._deliverySplit = deliverySplit;
  }

  get crossSellingViewmodels() {
    return this._crossSellingViewmodels;
  }

  set crossSellingViewmodels(data: CrossSellingProductBoxData[]) {
    this._crossSellingViewmodels = data;
  }

  get informativeBenefits() {
    return this._informativeBenefits;
  }

  set informativeBenefits(data: BasketInformativeBenefitItem[]) {
    this._informativeBenefits = data;
  }

  get greetingTextNotAllowedBasket() {
    return this._greetingTextNotAllowedBasket;
  }

  set greetingTextNotAllowedBasket(data: boolean) {
    this._greetingTextNotAllowedBasket = data;
  }

  get deliveryPromise() {
    return this._deliveryPromise;
  }

  set deliveryPromise(data: any) {
    this._deliveryPromise = data;
  }

  get basketGroupErrors(): {index: number, value: string, type: 'minValue'|'minNoOfBottles'}[] {
    return this._basketGroupErrors;
  }

  set milesAndMoreViewmodel(data: MilesAndMoreModel) {
    this._milesAndMoreViewmodel = data;
  }

  get milesAndMoreViewmodel() {
    return this._milesAndMoreViewmodel;
  }

  getBasketPositionGroupErrors() {
    const errors = [];

    this.basketItemGroups.forEach((group) => {
      if (group.minimumOrderValueFulFilled === false) {
        errors.push({
          value: group.minimumNoBottles?.toString() || group.minimumOrderValue,
          index: group.index,
          type: group.minimumNoBottles ? 'minNoOfBottles' : 'minValue',
        })
      }
    });

    return errors;
  }

  getIndexByGroupName(groupName: string): number|null {
    const group: BasketItemGroup = this.basketItemGroups.find((group: BasketItemGroup) => group.basketGroup === groupName);
    if (group && group.index) {
      return group.index;
    }

    return null;
  }
}

const basketStore: BasketStore = new BasketStore();
export default reactive(basketStore);

export {BasketStore}
